import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Code,
  CodeOutlined,
  Download,
  ExpandMore,
  FormatListNumbered,
  ViewCompactOutlined,
} from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography,
} from "@mui/material";

import PrimaryLayout from "../../components/layout/PrimaryLayout";
import DataTable from "../../components/tables/DataTable";
import { executionsApiClient } from "../../services/api-client";
import {
  V1DescribeWorkflowExecutionResponse,
  V1GetWorkflowExecutionHistoryReverseResponse,
  V1PendingActivityInfo,
} from "../../services/workflow-server";
import {
  columnsEventForCompact,
  columnsEventsReverse,
  columnsRun,
  CustomBoxForOutput,
  CustomIconButton,
  CustomInputOuputBox,
  CustomJSONBox,
  CustomToggleButton,
  CustomToggleButtonGroup,
  eventAttributesMap,
  getRowColor,
  handleCopy,
  LabelWithValue,
  PendingActivityInfoSection,
  renderActivityTaskCompleted,
  renderActivityTaskFailed,
  RenderActivityTaskScheduled,
  renderActivityTaskStarted,
  RenderContentForWorkflowExecutionStarted,
  RenderContentForWorkflowTaskScheduled,
  RenderDataForSelection,
  renderworkflowExecutionFailed,
  renderworkflowExecutionTerminated,
  renderWorkflowTaskFailed,
  renderworkflowTaskTimeout,
  TabOption
} from "./ExecutionsDetailsUtility";

const ExecutionDetails: React.FC = () => {
  const {
    ns: namespace,
    wid: executionWorkflowId,
    id: executionRunId,
  } = useParams();
  const [pendingActivityInfo, setPendingActivityInfo] = useState<V1PendingActivityInfo[]>([]);
  const [eventsData, setEventsData] =
    useState<V1GetWorkflowExecutionHistoryReverseResponse>({});
  const [InputData, setInputData] = useState<string[]>();
  const [OutPutData, setOutputData] = useState<string[]>();
  const [selectedTab, setSelectedTab] = useState<TabOption>("compact");

  const fetchRun = useCallback(async () => {
    try {
      if (executionWorkflowId && executionRunId) {
        const response =
          await executionsApiClient.api.workflowServiceDescribeWorkflowExecution(
            namespace || "default",
            executionWorkflowId,
            executionRunId
          );
        console.log("Workflow executions:", response.data);
        setPendingActivityInfo(response.data.pendingActivities || []);
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching workflow executions:", error);
    }
    return {
      workflowExecutionInfo: {}, // Provide a default structure here based on your API response
    } as V1DescribeWorkflowExecutionResponse;
  }, [executionRunId, executionWorkflowId, namespace])

  const fetchEventsReverse = useCallback(async () => {
    try {
      if (executionWorkflowId && executionRunId) {
        const response =
          await executionsApiClient.api.workflowServiceGetWorkflowExecutionHistoryReverse(
            namespace || "default",
            executionWorkflowId,
            executionRunId
          );
        console.log("Workflow events:", response.data);
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching workflow executions:", error);
    }
    return {
      workflowExecutionInfo: {},
    } as V1GetWorkflowExecutionHistoryReverseResponse;
  }, [executionRunId, executionWorkflowId, namespace]) 

  const fetchEvents = useCallback(async () => {
    try {
      if (executionWorkflowId && executionRunId) {
        const response =
          await executionsApiClient.api.workflowServiceGetWorkflowExecutionHistory(
            namespace || "default",
            executionWorkflowId,
            executionRunId
          );
        console.log("Workflow events:", response.data);
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching workflow executions:", error);
    }
    return {
      workflowExecutionInfo: {},
    } as V1GetWorkflowExecutionHistoryReverseResponse;
  }, [executionRunId, executionWorkflowId, namespace])

  const fetchEventsData = useCallback(async () => {
    try {
      if (executionWorkflowId && executionRunId) {
        const response =
          await executionsApiClient.api.workflowServiceGetWorkflowExecutionHistoryReverse(
            namespace || "default",
            executionWorkflowId,
            executionRunId
          );
        console.log("Workflow events Data:", response.data);
        setEventsData(response.data);
        const eventsLength = response.data.history?.events?.length ?? 0;
        console.log("Number of events:", eventsLength);
        const decodedDataArray =
          response.data.history?.events?.[eventsLength !== 0 ? eventsLength - 1 : 0]?.workflowExecutionStartedEventAttributes?.input?.payloads?.map(
            (payload) => {
              if (!payload?.data) return "";
              const decoded = atob(payload.data);
              try {
                return JSON.parse(decoded);
              } catch (e) {
                return decoded;
              }
            }
          ) || [];
        console.log("To check the response of the fetchEvent Data", response.data.history?.events?.[0])
        setInputData(decodedDataArray);
        console.log("response.data.history?.events?.[0]?.workflowExecutionCompletedEventAttributes?.result?.payloads", response.data.history?.events?.[0]
          ?.workflowExecutionCompletedEventAttributes?.result
          ?.payloads)
        const responseOutputData =
          response.data.history?.events?.[0]
            ?.workflowExecutionCompletedEventAttributes?.result
            ?.payloads?.map(
              (payload) => {
                if (!payload?.data) return "";
                const decoded = atob(payload.data);
                console.log("In the line 152", decoded);
                try {
                  return JSON.parse(decoded);
                } catch (e) {
                  return decoded;
                }
              }
            ) || ["Results will appear upon completion."];
        console.log("responseOutputData", responseOutputData);
        setOutputData(responseOutputData);
      }
    } catch (error) {
      console.error("Error fetching workflow executions:", error);
    }
    return {} as V1GetWorkflowExecutionHistoryReverseResponse;
  }, [executionRunId, executionWorkflowId, namespace])

  const dataExtractorRun = useCallback((
    response: V1DescribeWorkflowExecutionResponse
  ): any => {
    if (response) {
      return [
        {
          id: response.workflowExecutionInfo?.execution?.runId,
          status: response.workflowExecutionInfo?.status,
          workflowId: response.workflowExecutionInfo?.execution?.workflowId,
          runId: response.workflowExecutionInfo?.execution?.runId,
          type: response.workflowExecutionInfo?.type?.name || "",
          startTime: response.workflowExecutionInfo?.startTime,
          closeTime: response.workflowExecutionInfo?.closeTime,
          executionTime: response.workflowExecutionInfo?.executionTime,
          historyLength: response.workflowExecutionInfo?.historyLength,
          parentNamespaceId: response.workflowExecutionInfo?.parentNamespaceId,
          parentworkflowId:
            response.workflowExecutionInfo?.parentExecution?.workflowId || "",
          parentRunId:
            response.workflowExecutionInfo?.parentExecution?.runId || "",
          taskQueue: response.workflowExecutionInfo?.taskQueue,
          stateTransitionCount:
            response.workflowExecutionInfo?.stateTransitionCount,
          historySizeBytes: response.workflowExecutionInfo?.historySizeBytes,
        },
      ];
    } else {
      return [];
    }
  }, [])

  const dataExtractorEventsReverse = useCallback((
    response: V1GetWorkflowExecutionHistoryReverseResponse
  ): any[] => {
    return (response.history?.events || []).map((event) => {
      const id = event.eventId ?? "";
      const eventTime = event.eventTime ?? "";

      const attributeKey = event.eventType
        ? eventAttributesMap[event.eventType]
        : "";
      const attributes = attributeKey ? event[attributeKey] : null;

      return {
        id,
        eventTime,
        status: event.eventType,
        key: attributeKey,
        attributes,
      };
    });
  }, [])

  const filterActivityTaskEvents = useCallback((
    response: V1GetWorkflowExecutionHistoryReverseResponse
  ): any[] => {
    return (response.history?.events || [])
      .filter((event) => event.eventType === "ActivityTaskScheduled")
      .map((event) => {
        const id = event.eventId ?? "";
        const eventTime = event.eventTime ?? "";

        const attributeKey = event.eventType
          ? eventAttributesMap[event.eventType]
          : "";
        const attributes = attributeKey ? event[attributeKey] : null;

        return {
          id,
          eventTime,
          status: event?.activityTaskScheduledEventAttributes?.activityType?.name,
          key: attributeKey,
          attributes,
        };
      });
  }, [])

  const nextPageTokenExtractorEventsReverse = (
    response: V1GetWorkflowExecutionHistoryReverseResponse
  ): string | undefined => response.nextPageToken;

  const renderEventDetailsForCompact = (row: any) => {
    // console.log("attributes?.activityType?.name", row, eventsData);
    // console.log("rows data", row.id);
    return <RenderDataForSelection eventsData={eventsData} selectedRowId={row.id} />;
  };

  const renderEventDetails = useCallback((row: any) => {
    const { key, attributes } = row;
    // console.log(row);
    // console.log(eventsData);
    // console.log("key", key);
    const renderAttributes = () => {
      switch (key) {
        case "workflowExecutionCompletedEventAttributes":
          return (
            <Box>
              <Typography>Result</Typography>
              <CustomBoxForOutput>
                <CustomIconButton
                  onClick={() => {
                    handleCopy(
                      JSON.stringify(OutPutData ? OutPutData : [], null, 2)
                    );
                  }}
                >
                  <ContentCopyIcon />
                </CustomIconButton>
                <Typography
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap", color: "white" }}
                >
                  {JSON.stringify(OutPutData ? OutPutData : [], null, 2)}
                </Typography>
              </CustomBoxForOutput>
              <Divider />
              <LabelWithValue
                label="Workflow Task Completed Event ID"
                value={attributes?.workflowTaskCompletedEventId}
              />
            </Box>
          );

        case "workflowExecutionStartedEventAttributes":
          return <RenderContentForWorkflowExecutionStarted attributes={attributes} InputData={InputData || []} />

        case "workflowExecutionTerminatedEventAttributes":
          return renderworkflowExecutionTerminated(attributes);

        case "workflowExecutionFailedEventAttributes":
          return renderworkflowExecutionFailed(attributes);

        case "workflowTaskCompletedEventAttributes":
          return (
            <Box>
              <LabelWithValue
                label="Scheduled Event ID"
                value={attributes?.scheduledEventId}
              />
              <Divider />
              <LabelWithValue
                label="Started Event ID"
                value={attributes?.startedEventId}
              />
              <Divider />
              <LabelWithValue label="Identity" value={attributes?.identity} />
              <Divider />
              <LabelWithValue
                label="Worker Versioning ID"
                value={attributes?.workerVersioningId?.workerBuildId}
              />
              <Divider />
              <Typography>Sdk Metadata</Typography>
              <CustomBoxForOutput>
                <CustomIconButton
                  onClick={() => {
                    handleCopy(
                      JSON.stringify(attributes?.sdkMetadata, null, 2)
                    );
                  }}
                >
                  <ContentCopyIcon />
                </CustomIconButton>
                <Typography
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap", color: "white" }}
                >
                  {JSON.stringify(attributes?.sdkMetadata, null, 2)}
                </Typography>
              </CustomBoxForOutput>
              <Divider />
              <Typography>Metering Metadata</Typography>
              <CustomBoxForOutput>
                <CustomIconButton
                  onClick={() => {
                    handleCopy(
                      JSON.stringify(attributes?.meteringMetadata, null, 2)
                    );
                  }}
                >
                  <ContentCopyIcon />
                </CustomIconButton>
                <Typography
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap", color: "white" }}
                >
                  {JSON.stringify(attributes?.meteringMetadata, null, 2)}
                </Typography>
              </CustomBoxForOutput>
            </Box>
          );

        case "workflowTaskStartedEventAttributes":
          return (
            <Box>
              <LabelWithValue
                label="Scheduled Event ID"
                value={attributes?.scheduledEventId}
              />
              <Divider />
              <LabelWithValue label="Identity" value={attributes?.identity} />
              <Divider />
              <LabelWithValue label="RequestId" value={attributes?.requestId} />
              <Divider />
              <LabelWithValue
                label="Suggest Continue As New"
                value={attributes?.suggestContinueAsNew ? "True" : "False"}
              />
              <Divider />
              <LabelWithValue
                label="History Size Bytes"
                value={attributes?.historySizeBytes}
              />
            </Box>
          );

        case "workflowTaskScheduledEventAttributes":
          return <RenderContentForWorkflowTaskScheduled attributes={attributes} />

        case "workflowTaskTimedOutEventAttributes":
          return renderworkflowTaskTimeout(attributes);
        
        case "workflowTaskFailedEventAttributes":
          return renderWorkflowTaskFailed(attributes);

        case "activityTaskFailedEventAttributes":
          return renderActivityTaskFailed(attributes, row.eventTime);

        case "activityTaskCompletedEventAttributes":
          return renderActivityTaskCompleted(attributes);

        case "activityTaskStartedEventAttributes":
          return renderActivityTaskStarted(attributes);

        case "activityTaskScheduledEventAttributes":
          return <RenderActivityTaskScheduled attributes={attributes} />
      }
    };
    return <Box>{renderAttributes()}</Box>;
  }, [InputData, OutPutData, eventsData])

  const handleTabChange = (
    event: React.MouseEvent<HTMLElement>,
    newTab: TabOption | null
  ) => {
    if (newTab !== null) {
      setSelectedTab(newTab);
    }
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case "detailed":
        return (
          <DataTable
            columns={columnsEventsReverse}
            fetchData={fetchEventsReverse}
            dataExtractor={dataExtractorEventsReverse}
            nextPageTokenExtractor={nextPageTokenExtractorEventsReverse}
            getRowColor={getRowColor}
            expandable={true}
            renderExpandedContent={renderEventDetails}
          />
        );
      case "compact":
        return (
          <DataTable
            columns={columnsEventForCompact}
            fetchData={fetchEvents}
            dataExtractor={filterActivityTaskEvents}
            nextPageTokenExtractor={nextPageTokenExtractorEventsReverse}
            getRowColor={getRowColor}
            expandable={true}
            renderExpandedContent={renderEventDetailsForCompact}
          />
        );
      case "json":
        return (
          <Box
            sx={{
              backgroundColor: "black",
              m: 1,
              position: "relative",
              borderRadius: 2,
            }}
          >
            <CustomIconButton
              onClick={() => {
                handleCopy(JSON.stringify(eventsData.history, null, 2));
              }}
              sx={{ position: "absolute", top: 8, right: 8, mr: 2 }}
            >
              <ContentCopyIcon />
            </CustomIconButton>
            <CustomJSONBox>
              <Typography
                variant="body2"
                component="pre"
                sx={{ color: "white" }}
              >
                {JSON.stringify(eventsData.history, null, 2)}
              </Typography>
            </CustomJSONBox>
          </Box>
        );
    }
  };

  useEffect(() => {
    fetchEventsData();
  }, []);

  useEffect(() => {
    if (selectedTab === "download") {
      const blob = new Blob([JSON.stringify(eventsData.history, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "workflow-events.json";
      a.click();
      URL.revokeObjectURL(url);
      setSelectedTab("compact");
    }
  }, [selectedTab, eventsData]);

  return (
    <PrimaryLayout>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography sx={{ m: 2 }} variant="h6">
          Execution Info:
        </Typography>
      </Box>
      <Box>
        <DataTable
          columns={columnsRun}
          fetchData={fetchRun}
          dataExtractor={dataExtractorRun}
          orientation="transposed"
          size="small"
        />
      </Box>
      {pendingActivityInfo.length > 0 && (
        PendingActivityInfoSection(pendingActivityInfo)
      )}    
      <Box sx={{ m: 2 }}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography variant="h6">
            <CodeOutlined sx={{mr: "8px"}} /> 
            Input and Results
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              {/* Input Section */}
              <Box
                sx={{
                  flex: "1",
                  minWidth: "300px",
                }}
              >
                <Typography variant="h6" sx={{ ml: 2 }}>
                  Input
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "black",
                    m: 1,
                    position: "relative",
                    borderRadius: 2,
                  }}
                >
                  <CustomIconButton
                    onClick={() => {
                      handleCopy(JSON.stringify(InputData, null, 2));
                    }}
                    sx={{ position: "absolute", top: 8, right: 8, mr: 2 }}
                  >
                    <ContentCopyIcon />
                  </CustomIconButton>
                  <CustomInputOuputBox>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{ color: "white" }}
                    >
                      {JSON.stringify(InputData, null, 2)}
                    </Typography>
                  </CustomInputOuputBox>
                </Box>
              </Box>

              {/* Result Section */}
              <Box
                sx={{
                  flex: "1",
                  minWidth: "300px",
                }}
              >
                <Typography variant="h6" sx={{ ml: 3 }}>
                  Result
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "black",
                    m: 1,
                    position: "relative",
                    borderRadius: 2,
                  }}
                >
                  <CustomIconButton
                    onClick={() => {
                      handleCopy(JSON.stringify(OutPutData ? OutPutData : [], null, 2));
                    }}
                    sx={{ position: "absolute", top: 8, right: 8, mr: 2 }}
                  >
                    <ContentCopyIcon />
                  </CustomIconButton>
                  <CustomInputOuputBox>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{ color: "white" }}
                    >
                      {JSON.stringify(OutPutData ? OutPutData : [], null, 2)}
                    </Typography>
                  </CustomInputOuputBox>
                </Box>
              </Box>
            </Box>
        </AccordionDetails>
      </Accordion>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography sx={{ m: 2 }} variant="h6">
          Events:
        </Typography>
      </Box>
      <Box sx={{ m: 2 }}>
        <CustomToggleButtonGroup
          value={selectedTab}
          exclusive
          onChange={handleTabChange}
          aria-label="view options"
          size="small"
        >
          <CustomToggleButton value="compact" aria-label="compact view">
            <ViewCompactOutlined />
            <Typography variant="body2">Compact</Typography>
          </CustomToggleButton>
          <CustomToggleButton value="detailed" aria-label="detailed view">
            <FormatListNumbered />
            <Typography variant="body2">Detailed</Typography>
          </CustomToggleButton>
          <CustomToggleButton value="json" aria-label="json view">
            <Code />
            <Typography variant="body2">JSON</Typography>
          </CustomToggleButton>
          <CustomToggleButton value="download" aria-label="download view">
            <Download />
            <Typography variant="body2">Download</Typography>
          </CustomToggleButton>
        </CustomToggleButtonGroup>
      </Box>
      <Box>{renderTabContent()}</Box>
    </PrimaryLayout>
  );
};

export default ExecutionDetails;
